import {
  Header,
  SpaceBetween,
  Button,
  ColumnLayout,
  Modal,
  Tiles,
  TableProps,
  Table,
  FormField,
  Input,
  Box,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import {
  COMMENT_MAX_LENGTH,
  LABELS,
  TARGET_REP_NAME_KANA_COLUMN_LIST,
} from "./constants";
import { getMessage } from "../../features/error/messages";
import { isLengthValid } from "../../utils/inputValidation";
import TableEmptyState from "../UI/TableEmptyState";
import { addBlank, CODE_MASTER, COMMON_LABELS } from "../default-constants";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import {
  getHeaderCounterText,
  getIHRLabel,
  getPagedItems,
} from "../../utils/common";
import Pagination from "../UI/Pagination";
import Preference, { ContentOption } from "../UI/Preference";
import { ConfirmationStoreInfo } from "../../features/api-interfaces";
import {
  findRegisteredStore,
  getApiConfirmationResult,
  getComment,
  getConfirmationResultDict,
  isRequiresRepNameConfirm,
  isSameStore,
} from "./util";
import { isEqual, isUndefined } from "lodash";
import { useCodelistHook } from "../../app/useCodelistHook";

// 確認結果更新モーダル用
type ModalStore = {
  applicationStatusNo: number;
  individualHandlingReason: string;
};

interface IStore {
  registeredStoreList: ConfirmationStoreInfo[];
  storeList: ConfirmationStoreInfo[];
  setStoreList: Dispatch<SetStateAction<ConfirmationStoreInfo[] | undefined>>;
  resetStoreList: () => void;
}

/**
 * 個別取扱確認更新 店舗
 */
const Store = (props: IStore) => {
  const { registeredStoreList, storeList, setStoreList, resetStoreList } =
    props;
  const { individualHandlingReason } = useCodelistHook();
  const [pagedItems, setPagedItems] = useState<ConfirmationStoreInfo[]>([]);
  const [allItems, setAllItems] = useState<ConfirmationStoreInfo[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [bulkConfirmationStatus, setBulkConfirmationStatus] = useState("");
  const [visible, setVisible] = useState(false);
  const [modalStore, setModalStore] = useState<ModalStore>({
    applicationStatusNo: -1,
    individualHandlingReason: "",
  });
  const [errorMsgList, setErrorMsgList] = useState<
    { store: ModalStore; errorMsg: string }[]
  >([]);
  const CONFIRMATION_RESULT = addBlank(CODE_MASTER["CONFIRMATION_RESULT"]);

  useEffect(() => {
    setPagedItems(
      getPagedItems(
        storeList,
        currentPageIndex,
        pageSize
      ) as ConfirmationStoreInfo[]
    );
    setAllItems(storeList);
  }, [storeList, currentPageIndex, pageSize]);

  /**
   * コメントのイベントハンドラ.
   * @param {number} applicationStatusNo 更新対象の申請ステータス番号
   * @param {string} reason 更新対象の個別取扱事由
   * @param {string} inputValue 入力されたコメント
   */
  const commentOnChange = (
    applicationStatusNo: number,
    reason: string,
    inputValue: string
  ) => {
    const newItems = storeList.map((store) => {
      if (isSameStore(store, applicationStatusNo, reason)) {
        const errorMsg = isLengthValid(inputValue, COMMENT_MAX_LENGTH)
          ? ""
          : getMessage("KGRERR058", COMMENT_MAX_LENGTH).label;
        setErrorMsgList(
          errorMsgList.map((msg) => {
            if (isEqual(msg.store, modalStore)) {
              return { ...msg, errorMsg } as {
                store: ModalStore;
                errorMsg: string;
              };
            } else {
              return msg;
            }
          })
        );
        return { ...store, comment: inputValue } as ConfirmationStoreInfo;
      } else {
        return store;
      }
    });
    setStoreList(newItems);
  };

  /**
   * 確認結果の選択ボタンを押したときの処理.
   * @param {number} applicationStatusNo 更新対象の申請ステータス番号
   * @param {string} reason 更新対象の個別取扱事由
   */
  const confirmationResultOnClick = (
    applicationStatusNo: number,
    reason: string
  ) => {
    setVisible(true);
    setModalStore({ applicationStatusNo, individualHandlingReason: reason });
  };

  const baseColumnDefinitions: TableProps.ColumnDefinition<ConfirmationStoreInfo>[] =
    [
      {
        id: "individualHandlingReason",
        header: LABELS.INDIVIDUAL_HANDLING_REASON,
        cell: (item) =>
          getIHRLabel(individualHandlingReason, [
            item.individualHandlingReason,
          ]),
      },
      {
        id: "storeName",
        header: LABELS.STORE_NAME,
        cell: (item) => item.storeName,
      },
      {
        id: "corporateNumber",
        header: LABELS.CORPORATE_NUMBER,
        cell: (item) => (
          <>
            {item.individualCounselingTargetNo ? (
              <Link
                to={{
                  pathname: `/individual-counseling-project/${item.individualCounselingTargetNo}`,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.corporateNumber}
              </Link>
            ) : (
              <>{item.corporateNumber}</>
            )}
          </>
        ),
      },
      {
        id: "companyName",
        header: LABELS.COMPANY_NAME,
        cell: (item) => item.companyName,
      },
      {
        id: "repName",
        header: LABELS.REP_NAME,
        cell: (item) => item.repName,
      },
      {
        id: "tradeName",
        header: LABELS.MERCHANT_NAME,
        cell: (item) => item.tradeName,
      },
      {
        id: "repNameKanaApplication",
        header: LABELS.REP_NAME_KANA_APPLICATION,
        cell: (item) =>
          isRequiresRepNameConfirm(item.individualHandlingReason)
            ? item.repNameKanaApplication
            : COMMON_LABELS.HYPHEN,
      },
      {
        id: "repNameKanaTSR",
        header: LABELS.REP_NAME_KANA_TSR,
        cell: (item) =>
          isRequiresRepNameConfirm(item.individualHandlingReason)
            ? item.repNameKanaTSR
            : COMMON_LABELS.HYPHEN,
      },
      {
        id: "confirmationResult",
        header: LABELS.CONFIRMATION_RESULT,
        minWidth: 175,
        cell: (item) => (
          <SpaceBetween size={"xxxs"} direction={"horizontal"}>
            <Box float={"left"}>
              <div style={{ width: "60px", marginTop: "6px" }}>
                {getConfirmationResultDict(item.confirmationResult).label}
              </div>
            </Box>
            <Box float={"right"}>
              <Button
                variant={"normal"}
                onClick={() =>
                  confirmationResultOnClick(
                    item.applicationStatusNo,
                    item.individualHandlingReason
                  )
                }
                disabled={
                  !isUndefined(
                    findRegisteredStore(
                      registeredStoreList,
                      item.applicationStatusNo,
                      item.individualHandlingReason
                    )
                  )
                }
              >
                {LABELS.SELECT}
              </Button>
            </Box>
          </SpaceBetween>
        ),
      },
      {
        id: "comment",
        header: LABELS.COMMENT,
        cell: (item) => (
          <FormField
            errorText={
              errorMsgList.find(
                ({ store }) =>
                  isEqual(
                    store.applicationStatusNo,
                    item.applicationStatusNo
                  ) &&
                  isEqual(
                    store.individualHandlingReason,
                    item.individualHandlingReason
                  )
              )?.errorMsg
            }
          >
            <Input
              value={item.comment ?? ""}
              onChange={({ detail }) => {
                commentOnChange(
                  item.applicationStatusNo,
                  item.individualHandlingReason,
                  detail.value
                );
              }}
              disabled={
                !isUndefined(
                  findRegisteredStore(
                    registeredStoreList,
                    item.applicationStatusNo,
                    item.individualHandlingReason
                  )
                )
              }
            />
          </FormField>
        ),
      },
    ];
  const isRequiresRepConfirm = storeList.some((store) => {
    return isRequiresRepNameConfirm(store.individualHandlingReason);
  });
  const columnDefinitions = isRequiresRepConfirm
    ? baseColumnDefinitions
    : baseColumnDefinitions.filter(
        (column) => !TARGET_REP_NAME_KANA_COLUMN_LIST.includes(column.id!)
      );
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  })) as ContentOption[];

  /**
   * 一括確認結果の入力ハンドラ.
   * @param {string} confirmationResultValue 確認結果value
   */
  const confirmationResultBulkCheck = (confirmationResultValue: string) => {
    const newItems = storeList.map((store) => {
      const registeredStore = findRegisteredStore(
        registeredStoreList,
        store.applicationStatusNo,
        store.individualHandlingReason
      );
      if (registeredStore) {
        // 確認結果が登録済みの個別取扱事由はこのハンドラで変更しない
        return registeredStore;
      } else {
        return {
          ...store,
          confirmationResult: getApiConfirmationResult(confirmationResultValue),
          comment: getComment(confirmationResultValue, store.comment),
        } as ConfirmationStoreInfo;
      }
    });
    setStoreList(newItems);
  };

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setBulkConfirmationStatus("");
    resetStoreList();
  };

  /**
   * 確認結果の入力ハンドラ.
   * @param {ModalStore} modalStore 更新対象の店舗情報
   * @param {boolean} confirmationResultValue 入力された確認結果value
   */
  const confirmationResultCheck = (
    modalStore: ModalStore,
    confirmationResultValue: string
  ) => {
    setVisible(false);
    const newItems = storeList.map((store) => {
      if (
        isSameStore(
          store,
          modalStore.applicationStatusNo,
          modalStore.individualHandlingReason
        )
      ) {
        return {
          ...store,
          confirmationResult: getApiConfirmationResult(confirmationResultValue),
          comment: getComment(confirmationResultValue, store.comment),
        } as ConfirmationStoreInfo;
      } else {
        return store;
      }
    });
    setStoreList(newItems);
  };

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) => {
    setPageSize(detail.pageSize!);
    setCurrentPageIndex(1);
  };

  return (
    <SpaceBetween direction={"vertical"} size={"s"}>
      <Table
        columnDefinitions={columnDefinitions}
        items={pagedItems}
        empty={<TableEmptyState />}
        resizableColumns
        sortingDescending
        header={
          <SpaceBetween size={"xs"}>
            <Header>
              {LABELS.STORE}
              {getHeaderCounterText(allItems.length, pagedItems.length)}
            </Header>
            <ColumnLayout columns={3}>
              <Tiles
                onChange={({ detail }) => {
                  setBulkConfirmationStatus(detail.value);
                  if (detail.value) {
                    confirmationResultBulkCheck(detail.value);
                  }
                }}
                value={bulkConfirmationStatus}
                items={[
                  {
                    label: LABELS.BULK_CONFIRMATION_RESULT_OK,
                    value: CONFIRMATION_RESULT[1].value,
                  },
                  {
                    label: LABELS.BULK_CONFIRMATION_RESULT_NG,
                    value: CONFIRMATION_RESULT[2].value,
                  },
                ]}
              />
              <Button variant={"normal"} onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </ColumnLayout>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={{ pageSize, visibleContent: [] }}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
      />
      <Modal
        onDismiss={() => setVisible(false)}
        visible={visible}
        size={"small"}
        footer={
          <Box float="right">
            <Button
              variant={"normal"}
              onClick={() => confirmationResultCheck(modalStore, "")}
            >
              {COMMON_LABELS.BTNS.CLEAR}
            </Button>
          </Box>
        }
        header={LABELS.CONFIRMATION_RESULT}
      >
        <SpaceBetween size={"xxl"} direction={"horizontal"}>
          <Button
            variant={"normal"}
            onClick={() =>
              confirmationResultCheck(modalStore, CONFIRMATION_RESULT[1].value)
            }
          >
            {CONFIRMATION_RESULT[1].label}
          </Button>
          <Button
            variant={"normal"}
            onClick={() =>
              confirmationResultCheck(modalStore, CONFIRMATION_RESULT[2].value)
            }
          >
            {CONFIRMATION_RESULT[2].label}
          </Button>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default Store;
